import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@chancenlogistik/client/components/logistik-component-cart-list';
import '@chancenlogistik/client/components/logistik-component-shipping-details';
import type { LogistikComponentShippingDetails } from '@chancenlogistik/client/components/logistik-component-shipping-details';
import { placeOrder } from '@chancenlogistik/client/helpers/place-order';
import { LogistikCartItem } from '@chancenwerk/db/LogistikCart.js';
import { LogistikOrderShippingDetails } from '@chancenwerk/db/LogistikOrder.js';
import {
  AdvertisingMaterial,
  ChancenBrief,
  EventTicket,
  Furnishing,
  LearningMaterial,
  LogistikProduct,
  WorkMaterial,
  WorkshopPack,
} from '@chancenwerk/db/LogistikProduct.js';
import { LogistikProductCategory } from '@chancenwerk/db/enums/LogistikEnums.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import type { ChromiumDropdownSearchSelection } from '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import { html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { createRef, ref } from 'lit/directives/ref.js';
import type { DateTime } from 'luxon';

/**
 * @element logistik-dialog-add-manual-order
 */
@customElement('logistik-dialog-add-manual-order')
export class LogistikDialogAddManualOrder extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-dialog-add-manual-order';
  }

  override heading = 'Manuelle Bestellung anlegen';

  @state() private _state: 'info' | 'products' | 'checkout' | 'done' = 'info';

  @state() private readonly _products = new RXController(
    this,
    LogistikProduct.getAll<LogistikProduct>()({ title: 1, _class: 1, category: 1 }),
  );
  @state() private readonly _selectedItems: LogistikCartItem[] = [];

  private readonly _shippingDetailsRef = createRef<LogistikComponentShippingDetails>();
  private _orderDate?: DateTime;

  override content(): TemplateResult {
    return html`
      <x-flex padding="md">
        ${choose(
          this._state,
          [
            [
              'info',
              () =>
                html`
                  <x-flex space="md" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
                    <cr-icon
                      ${css({ alignSelf: 'center', color: this.colors.tone.warning, fontSize: this.spacing.xl })}
                    >
                      warning
                    </cr-icon>
                    <ul>
                      <x-flex space="md">
                        <li>
                          <cr-text>
                            Diese Funktion sollte nur zum Nachtragen von getätigten Bestellungen genutzt werden, die
                            nicht über die Logistik-Plattform eingegangen sind.
                          </cr-text>
                        </li>

                        <li><cr-text> Hier getätigte Bestellungen versenden keine Mails an den Empfänger </cr-text></li>
                      </x-flex>
                    </ul>
                  </x-flex>
                `,
            ],
            ['products', () => this._productSelection()],
            ['checkout', () => this._checkoutDetails()],
            [
              'done',
              () =>
                html`
                  <x-flex space="md" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
                    <cr-h4> Bestellung erfolgreich angelegt </cr-h4>
                    <cr-text> Für diese Bestellung gehen keine Mails an den Empfänger! </cr-text>
                  </x-flex>
                `,
            ],
          ],
          () => html` 404 `,
        )}
      </x-flex>
    `;
  }

  protected override footerContent() {
    return choose(
      this._state,
      [
        [
          'info',
          () => html`
            <x-flex horizontal space="md" space-between>
              <cr-button mode="outline" @click=${() => this.close()}> Abbrechen </cr-button>
              <cr-button @click=${() => (this._state = 'products')}> Verstanden </cr-button>
            </x-flex>
          `,
        ],
        [
          'products',
          () =>
            html`
              <x-flex horizontal space="md" space-between>
                <cr-button mode="outline" @click=${() => this.close()}> Abbrechen </cr-button>
                <cr-button
                  @click=${() => {
                    if (this._selectedItems.length) this._state = 'checkout';
                    else
                      void ChromiumDialog.alert(
                        'Um eine manuelle Bestellung anzulegen muss mindestens ein Produkt hinzugefügt werden!',
                      );
                  }}
                >
                  Weiter zu Nutzerdetails
                </cr-button>
              </x-flex>
            `,
        ],
        [
          'checkout',
          () => html`
            <x-flex horizontal space="md" space-between>
              <cr-button mode="outline" @click=${() => (this._state = 'products')}> Zurück </cr-button>
              <cr-button @click=${() => this._createOrderManually()}> Neue Bestellung anlegen </cr-button>
            </x-flex>
          `,
        ],
        [
          'done',
          () => html`
            <x-flex horizontal end>
              <cr-button @click=${() => this.close()}> Abschließen </cr-button>
            </x-flex>
          `,
        ],
      ],
      () => html` 404 `,
    );
  }

  override async close() {
    if (
      !this._selectedItems.length ||
      this._state === 'done' ||
      (await ChromiumDialog.confirm('Möchten Sie die eingegeben Daten wirklich verwerfen?'))
    )
      return super.close();
  }

  private _productSelection() {
    return html`
      <x-flex space="lg">
        <cr-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
          Wählen Sie die Produkte für die manuelle Bestelung aus und geben Sie die Mengen in der Warenkorb Ansicht an:
        </cr-text>
        <cr-dropdown-search-selection
          placeholder="Produkt Suchen..."
          .search=${(str: string) =>
            str
              ? (this._products.value ?? []).filter(product =>
                  new RegExp(str.replace(/([^\w]{1})/g, '\\$1'), 'i').test(product.title),
                )
              : this._products.value ?? []}
          .renderItem=${(product?: LogistikProduct) =>
            html`
              <cr-flex space="xs" padding="sm">
                <cr-text> ${product?.title} </cr-text> <cr-text tone="subtle"> ${product?._class} </cr-text>
              </cr-flex>
            `}
          .renderString=${(product?: LogistikProduct) => product?.title ?? ''}
          @value-picked=${(e: ValueEvent<LogistikProduct>) => {
            this._selectedItems.push(
              new LogistikCartItem({
                productID: e.detail.value._id,
                quantity: 1,
                productCategory: {
                  [AdvertisingMaterial._class]: LogistikProductCategory.ADVERTISING_MATERIAL,
                  [LearningMaterial._class]: LogistikProductCategory.LEARNING_MATERIAL,
                  [WorkMaterial._class]: LogistikProductCategory.WORK_MATERIAL,
                  [EventTicket._class]: LogistikProductCategory.TICKETS,
                  [Furnishing._class]: LogistikProductCategory.FURNISHING,
                  [ChancenBrief._class]: LogistikProductCategory.CHANCEN_BRIEF,
                  [WorkshopPack._class]: LogistikProductCategory.WORKSHOP_PACK,
                }[e.detail.value._class],
              }),
            );
            (e.currentTarget as ChromiumDropdownSearchSelection<any, any>).value.next(null);
            this.requestUpdate();
          }}
        ></cr-dropdown-search-selection>
        <logistik-component-cart-list
          .cartItems=${this._selectedItems}
          @amount-changed=${(e: CustomEvent<{ productID: string; amount: number }>) => {
            const item = this._selectedItems.find(item => item.productID === e.detail.productID);
            if (item) item.quantity = e.detail.amount;
          }}
          @item-removed=${(e: CustomEvent<{ productID: string }>) => {
            const index = this._selectedItems.findIndex(item => item.productID === e.detail.productID);
            if (index > -1) {
              this._selectedItems.splice(index, 1);
              this.requestUpdate();
            }
          }}
        ></logistik-component-cart-list>
      </x-flex>
    `;
  }

  private _checkoutDetails() {
    return html`
      <x-flex space="lg">
        <x-flex space="md">
          <cr-text>
            Falls diese Bestellung nachgetragen wird, können Sie hier das ursprüngliche Bestelldatum angeben:
          </cr-text>
          <cr-input
            type="date"
            placeholder="Bestelldatum"
            @value-picked=${(e: ValueEvent<DateTime>) => (this._orderDate = e.detail.value)}
          ></cr-input>
        </x-flex>
        <cr-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
          Geben Sie die Lieferdaten für den Empfänger an:
        </cr-text>
        <logistik-component-shipping-details
          ${ref(this._shippingDetailsRef)}
          .value=${new LogistikOrderShippingDetails({})}
          is-manually-created
        ></logistik-component-shipping-details>
      </x-flex>
    `;
  }

  private async _createOrderManually() {
    const shippingDetails = await this._shippingDetailsRef.value?._form.validate();
    if (!shippingDetails) {
      logger.error({ form: this._shippingDetailsRef.value }, 'Bestellung konnte nicht erstellt werden');
      void ChromiumDialog.alert('Bestellung konnte nicht erstellt werden. Bitte versuchen Sie es später erneut!');
      return;
    }

    try {
      await ChromiumDialog.waitFor(
        placeOrder(this._selectedItems, shippingDetails, { isManual: true, orderDate: this._orderDate }),
      );
    } catch (e) {
      logger.error({ err: e }, 'Bestellung konnte nicht erstellt werden (placeOrder)');
      void ChromiumDialog.alert('Bestellung konnte nicht erstellt werden. Bitte versuchen Sie es später erneut!');
    }

    this._state = 'done';
  }
}
